import { Dispatch, SetStateAction } from 'react';

import { Button } from '../base/Button';
import { Modal } from '../../layouts/Modal';
import { TextField } from '../base/TextField';
import { SectionLoading } from '../shared/SectionLoading';
import {
  DataPointFormFields,
  IDataPoint,
} from '../../types/DataPoint/DataPoint';
import useSuperAdminDataPointMutations from '../../data/datapoint/superadmin/useSuperAdminDataPointMutation';
import { FormAutoCompleteCombobox } from '../base/Form/FormAutoCompleteCombobox';
import { Form } from '../base/Form/Form';
import { FormToggle } from '../base/Form/FormToggle';
import useApplianceTypes from '../../data/appliance/useApplianceTypes';

export interface CreateEditDataPointProps {
  open: boolean;
  onClose: Dispatch<SetStateAction<boolean>>;
  dataPoint?: IDataPoint;
}

export function CreateEditDataPoint({
  open,
  onClose,
  dataPoint,
}: CreateEditDataPointProps) {
  const { applianceTypes } = useApplianceTypes();
  const { createDataPoint, updateDataPoint } =
    useSuperAdminDataPointMutations();

  const handleClose = () => {
    onClose(false);
  };

  const handleCreate = (data: DataPointFormFields) =>
    createDataPoint.mutate(
      {
        ...data,
        hardwareTypeIds: data.hardwareTypeIds.map((ht) => ht.value),
      },
      {
        onSuccess: () => onClose(false),
      }
    );

  const handleUpdate = (data: DataPointFormFields) =>
    updateDataPoint.mutate(
      {
        ...dataPoint,
        ...data,
        hardwareTypeIds: data.hardwareTypeIds.map((ht) => ht.value),
      },
      {
        onSuccess: () => onClose(false),
      }
    );

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={`${dataPoint ? 'Edit' : 'Create'} Data Point`}
      description={`Use this form to ${
        dataPoint ? 'edit' : 'create'
      } a data point`}
    >
      {applianceTypes.isLoading ? (
        <SectionLoading />
      ) : (
        <Form<DataPointFormFields>
          defaultValues={
            dataPoint
              ? {
                  ...dataPoint,
                  hardwareTypeIds: dataPoint.hardwareTypeIds.map((ht) => ({
                    value: ht,
                    label: applianceTypes.data?.find((t) => t.id === ht)?.name,
                  })),
                }
              : {
                  icon: 'fas,',
                }
          }
          onSubmit={dataPoint ? handleUpdate : handleCreate}
          className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-y-4 sm:gap-6 sm:gap-y-4"
        >
          {({ register, control }) => (
            <>
              <TextField
                {...register('name', { required: true })}
                label="Name"
              />
              <TextField {...register('unit')} label="Unit" />
              <TextField {...register('icon')} label="Icon" />
              <div className="pt-5 flex flex-col row-span-2 space-y-3">
                <FormToggle
                  toggleProps={{ label: 'Enable Graph' }}
                  control={control}
                  {...register('graphEnable')}
                />
                <FormToggle
                  toggleProps={{ label: 'Enable Archive' }}
                  control={control}
                  {...register('archiveEnable')}
                />
                <FormToggle
                  toggleProps={{ label: 'Core Data Point' }}
                  control={control}
                  {...register('coreDatapoint')}
                />
                <FormToggle
                  toggleProps={{ label: 'System Only' }}
                  control={control}
                  {...register('systemOnly')}
                />
                <FormToggle
                  toggleProps={{ label: 'Default Hidden' }}
                  control={control}
                  {...register('defaultHidden')}
                />
              </div>
              <FormAutoCompleteCombobox
                {...register('hardwareTypeIds')}
                label="Hardware Types"
                options={
                  applianceTypes.data?.map((type) => ({
                    value: type.id,
                    label: type.name,
                  })) ?? []
                }
              />
              <div className="mt-6 sm:col-span-2 space-x-4">
                <Button type="submit">{dataPoint ? 'Update' : 'Create'}</Button>
                <Button variant="outlined" onClick={handleClose}>
                  Cancel
                </Button>
              </div>
            </>
          )}
        </Form>
      )}
    </Modal>
  );
}
